import { useEffect, useState } from "react";
import { Wifi, WifiOff, CloudUpload, MonitorSmartphone, Loader2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  getQueueCount,
  isOnline,
  subscribeToQueue,
  flushQueue,
} from "@/lib/offlineQueue";
import { toast } from "sonner";

/**
 * Sticky banner shown when in Kiosk Mode on public reflection / feedback pages.
 * Displays:
 *  - Kiosk mode indicator
 *  - Online / offline status
 *  - Pending sync count + manual "Sync now" button
 */
const KioskBanner = ({ label = "Kiosk Mode" }) => {
  const [online, setOnline] = useState(isOnline());
  const [count, setCount] = useState(getQueueCount());
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const unsub = subscribeToQueue(({ online: o, queueCount }) => {
      setOnline(o);
      setCount(queueCount);
    });
    return unsub;
  }, []);

  // Auto-flush queue when we come back online.
  useEffect(() => {
    if (online && count > 0 && !syncing) {
      void doSync(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [online]);

  const doSync = async (silent = false) => {
    if (syncing) return;
    setSyncing(true);
    try {
      const res = await flushQueue();
      setCount(getQueueCount());
      if (!silent || res.synced > 0) {
        if (res.synced > 0) {
          toast.success(`Synced ${res.synced} pending submission${res.synced === 1 ? "" : "s"}`);
        } else if (!silent) {
          toast.info("Nothing to sync");
        }
      }
    } catch (e) {
      if (!silent) toast.error("Sync failed — will retry automatically");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div
      className="sticky top-0 z-40 border-b border-[#C28A2E]/40 bg-[#2A1810] text-white"
      data-testid="kiosk-banner"
    >
      <div className="max-w-3xl mx-auto px-4 py-2.5 flex items-center justify-between gap-3 text-sm">
        <div className="flex items-center gap-2 min-w-0">
          <div className="inline-flex items-center gap-1.5 bg-[#C28A2E]/25 text-[#F5D88A] px-2 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-bold">
            <MonitorSmartphone className="w-3 h-3" /> {label}
          </div>
          <span className="hidden sm:inline text-xs text-white/70 truncate">Pass this device around to participants</span>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {online ? (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#A8D49A]" data-testid="kiosk-online">
              <Wifi className="w-3.5 h-3.5" /> Online
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs text-[#F5D88A]" data-testid="kiosk-offline">
              <WifiOff className="w-3.5 h-3.5" /> Offline — saving locally
            </span>
          )}
          {count > 0 && (
            <Button
              size="sm"
              variant="secondary"
              onClick={() => doSync(false)}
              disabled={syncing || !online}
              className="h-7 px-2.5 text-xs bg-[#C28A2E] hover:bg-[#A6741F] text-white border-0"
              data-testid="kiosk-sync-btn"
            >
              {syncing ? (
                <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
              ) : (
                <CloudUpload className="w-3.5 h-3.5 mr-1" />
              )}
              {count} pending
            </Button>
          )}
          {count === 0 && online && (
            <span className="inline-flex items-center gap-1 text-[10px] text-white/50">
              <CheckCircle2 className="w-3 h-3" /> Synced
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default KioskBanner;
